import { API, Role } from '@thuas/pd-schemas';
import api from 'helpers/api';
import { Retrieving, callGet } from 'helpers/apiTypes';
import { convertToRoman } from 'helpers/helpers';
import { prepareForBackend } from 'helpers/objects';

export { Role } from '@thuas/pd-schemas';
export type RoleType = (typeof Role)[keyof typeof Role];
export type IUser = API['User']['read'];
export type IUserWithIsGuest = IUser & { isGuest?: boolean };
export type IUserCreate = API['User']['create'];
export type IUserUpdate = API['User']['update'] & {
  moderatesProjectsOverwrite?: { id: number }[];
  moderatesDialoguesOverwrite?: { id: number }[];
  subscribedToDialoguesOverwrite?: { id: number }[];
  subscribedToProjectsOverwrite?: { id: number }[];
};
export type IAuthorName = {
  id: number;
  username: string;
  authorname: string;
  seq?: number;
};

function prepareForUpdate(user: IUser): IUserUpdate {
  const updated = prepareForBackend(user, [
    'logs',
    'lastLogin',
    'failedLogins',
    'lockedUntil',
    'createdAt',
  ]) as IUserUpdate;
  return updated;
}
function prepareForCreate(user: IUserCreate): IUserCreate {
  return {
    ...user,
    about: '',
    // avatar: null,
    // member: null,
    // pages: [],
    // projects: [],
    // publications: [],
    // uploadedFiles: [],
  };
}

function getAuthorname(author: IAuthorName) {
  if (author.seq) return author.username + ' ' + convertToRoman(author.seq);
  return author.username;
}
export function createAuthorName(user: IUser): IAuthorName {
  return {
    id: user.id!,
    username: user.username!,
    get authorname() {
      return getAuthorname(this);
    },
    seq: 0,
  };
}

export async function loginUser(data: any) {
  try {
    const response = await api.post('Session', {
      Email: data.email,
      Password: data.password,
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function userForgot(data: any) {
  try {
    const response = await api.patch('Account', {
      Email: data.email,
      Action: 'send-password-reset-mail',
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function resetPwd(data: any) {
  try {
    // we use the token parameter from the url to receive the token (see App.js)
    const response = await api.patch('Account', {
      // Email: data.email,
      Token: data.token,
      Password: data.password,
      Action: 'update-password',
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function verifyMail(data: any) {
  try {
    // we use the token parameter from the url to receive the token (see App.js)
    const response = await api.patch('Account', {
      Token: data.token,
      RegistrationCode: data.regcode,
      Action: 'verify-token-and-user',
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function postVerificationCode(data: any) {
  try {
    const response = await api.post('verify', {
      userId: data.userId,
      verificationCode: data.code,
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function postUserData(data: any, noEmail: boolean) {
  try {
    const response = await api.post('User', prepareForCreate(data), {
      noEmail,
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function patchUserData(data: IUser) {
  try {
    const response = await api.patch('User/' + data.id, prepareForUpdate(data));
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function fetchUser(id: number) {
  try {
    const query = {
      '*': true,
      avatar: {
        id: true,
        uri: true,
      },
      logs: {
        '*': true,
      },
    } as const satisfies Retrieving<'User'>;
    const response = await callGet('User', query, id);
    return response.response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function fetchUserNames() {
  try {
    const query = {
      id: true,
      username: true,
      email: true,
    } as const satisfies Retrieving<'User'>;
    const response = await callGet('User', query);
    return response.response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}
